/**
 * Enviar documentos antes de o caso existir (E144.F3, GUS-135, ADR-0098; E165.F1.T03, HTML 6.8), na
 * ordem do molde: a identificação, o tipo de exame, o escritório (banco), os arquivos e as
 * observações. Na abertura pela operação, a origem do caso vem antes de tudo (E177.F1, ADR-0131): o
 * escritório requerente, opcional; o banco sai da leitura da CCB.
 *
 * Na abertura do cliente no fluxo eletrônico, num processo novo, a identificação (processo, pasta e
 * CPF) é obrigatória: incompleta, a área de arquivos fica travada, com o aviso do porquê. No exame de
 * um processo existente a identificação é a dele, e o que faltar se completa com o caso aberto.
 *
 * E177.F4 (GUS-253, ADR-0130): a tela não recebe arquivo — os do Contrato Digital vão na tela
 * "Contratos digitais", um bloco por CCB. O caso de entrada nasce no "Continuar", e não ao abrir a
 * tela: quem só olha não deixa caso vazio (e o rascunho sem arquivo não aparece na lista, E177.F2).
 * O pedido vai ao processo junto; o "Contrato Não Digital" segue o envio de sempre. O caso aberto fica
 * guardado: tentar de novo não abre outro.
 */
import { useState } from 'react';
import type { AxiosInstance } from 'axios';

import { ptBR } from '@/i18n/pt-BR';
import {
  escolheEscritorio,
  identificacaoDoProcesso,
  modalidadeDoServico,
  payloadDoPasso,
  PEDIDO_VAZIO,
  pedidoDoProcesso,
  type FormularioDoEnvio,
  type ServicoDaAbertura,
} from '@/lib/abertura';
import { createCase, type CaseRecordData } from '@/lib/cases';
import type { ProcessoData } from '@/lib/processos';

import { CartoesDeServico } from './CartoesDeServico';
import { EscritorioDoBanco } from './EscritorioDoBanco';
import { FormularioDaIdentificacao } from './FormularioDaIdentificacao';
import { CartaoDaOrigem, useOrigemDaOperacao } from './OrigemDoCaso';
import { ObservacoesDoPedido } from './PedidoDoCliente';
import { AvisosDoEnvio, RodapeDoEnvio } from './RodapeDoEnvio';
import {
  errosDoFormulario,
  gravarPedidoNoProcesso,
  mensagemDoErroAoAbrir,
  pedeEscritorio,
} from './regrasDoEnvio';

const t = ptBR.abertura;

export interface EnviarSemCasoProps {
  client: AxiosInstance;
  onAberto: (caso: CaseRecordData) => void;
  onVoltar: () => void;
  doBanco?: boolean;
  /** E165.F1.T03: a abertura do cliente (banco e escritório), com a identificação obrigatória. */
  doCliente?: boolean;
  /** E177.F1 (ADR-0131): a abertura pela operação (plataforma), com a origem do caso. */
  daOperacao?: boolean;
  /** E126.F4: mais um exame num processo existente — a pasta e o número vêm dele. */
  processo?: ProcessoData;
  servicoInicial?: ServicoDaAbertura;
}

function formularioInicial(processo: ProcessoData | undefined): FormularioDoEnvio {
  // No processo existente a identificação é a dele: gravar o pedido não a apaga. A abertura não
  // pergunta data nenhuma (E163.F1, ADR-0112 §4): a data do ato fica na edição do caso.
  return {
    identificacao: identificacaoDoProcesso(processo),
    pedido: processo !== undefined ? pedidoDoProcesso(processo) : PEDIDO_VAZIO,
  };
}

export function EnviarSemCaso(props: EnviarSemCasoProps): JSX.Element {
  const { client, doBanco = false, doCliente = false, processo } = props;
  // E176.F1 (ADR-0129): o banco escolhe os mesmos serviços que o escritório.
  const [servico, setServico] = useState<ServicoDaAbertura>(
    () => props.servicoInicial ?? 'eletronica',
  );
  const [escritorio, setEscritorio] = useState<string | null>(null);
  const [escritorioInvalido, setEscritorioInvalido] = useState(false);
  const [formulario, setFormulario] = useState(() => formularioInicial(processo));
  const [tentou, setTentou] = useState(false);
  const [abrindo, setAbrindo] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [aberto, setAberto] = useState<CaseRecordData | null>(null);
  const eletronica = servico === 'eletronica';
  const comEscritorio = escolheEscritorio(doBanco, processo);
  const obrigatoria = doCliente && eletronica && processo === undefined;
  const erros = errosDoFormulario(formulario, obrigatoria);
  // E177.F1: no processo novo da operação, o escritório requerente; no existente, o do processo.
  const origem = useOrigemDaOperacao(props.daOperacao, processo);
  // O tipo de exame, o escritório e a origem não mudam depois de o caso abrir.
  const travado = abrindo || aberto !== null;

  async function abrir(): Promise<void> {
    setTentou(true);
    setErro(null);
    const modalidade = modalidadeDoServico(servico);
    if (modalidade === null || erros.algum) return;
    setAbrindo(true);
    setEscritorioInvalido(false);
    let caso = aberto;
    try {
      caso ??= await createCase(
        client,
        payloadDoPasso(modalidade, {
          processo,
          identificacao: formulario.identificacao,
          escritorioDoBanco: comEscritorio ? escritorio : undefined,
          prazo: formulario.pedido.prazo,
          escritorioRequerente: origem.doPasso,
        }),
      );
      setAberto(caso);
    } catch (err) {
      setEscritorioInvalido(pedeEscritorio(err));
      setErro(mensagemDoErroAoAbrir(err));
      setAbrindo(false);
      return;
    }
    try {
      await gravarPedidoNoProcesso(client, caso, formulario);
      props.onAberto(caso);
    } catch {
      setErro(t.erroAoGravarPedido);
      setAbrindo(false);
    }
  }

  return (
    <form
      onSubmit={(evt) => {
        evt.preventDefault();
        void abrir();
      }}
      className="flex flex-col gap-5"
      noValidate
      data-testid="abertura-enviar"
      aria-describedby={erro !== null ? 'abertura-erro' : undefined}
    >
      <CartaoDaOrigem client={client} origem={origem} disabled={travado} />
      <FormularioDaIdentificacao
        valor={formulario}
        onChange={setFormulario}
        erros={erros}
        tentou={tentou}
        obrigatoria={obrigatoria}
        processo={processo}
        disabled={abrindo}
      />
      <CartoesDeServico escolhido={servico} onEscolher={setServico} disabled={travado} />
      {comEscritorio ? (
        <EscritorioDoBanco
          client={client}
          valor={escritorio}
          onChange={(id) => {
            setEscritorio(id);
            setEscritorioInvalido(false);
          }}
          invalido={escritorioInvalido}
          disabled={travado}
        />
      ) : null}
      <ObservacoesDoPedido
        valor={formulario.pedido}
        onChange={(pedido) => setFormulario({ ...formulario, pedido })}
        disabled={abrindo}
      />
      <AvisosDoEnvio erro={erro} />
      <RodapeDoEnvio
        abrindo={abrindo}
        travado={obrigatoria && erros.identificacaoIncompleta}
        onVoltar={props.onVoltar}
      />
    </form>
  );
}
